// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Navigation Utility Functions
 * Pure functions for distance calculations, warp mechanics, and display helpers
 */

import * as ColorUtils from '../ui/ColorUtils';
import GameConfig from '../../config/GameConfig';
import { getDifficultyMultipliers } from '../DifficultyUtils';
import { getMasteryBonus } from './PermanentProgressManager';

/**
 * Calculate distance between two star systems using their map positions
 * Simple 3D Euclidean distance - what you see is what you get!
 * @param {Object} fromSystem - Source system object
 * @param {Object} toSystem - Destination system object
 * @param {Object} distancePositions - Object mapping system IDs to {x, y, z} positions
 * @returns {number} Distance in light-years
 */
export const calculateDistance = (fromSystem, toSystem, distancePositions) => {
  if (!fromSystem || !toSystem || fromSystem.id === toSystem.id) return 0;
  
  // Check if systems have x/y/z coordinates directly (e.g., stranded in deep space)
  // If so, use those instead of looking up in distancePositions
  const fromPos = (fromSystem.x !== undefined && fromSystem.y !== undefined) 
    ? { x: fromSystem.x, y: fromSystem.y, z: fromSystem.z || 0 }
    : distancePositions[fromSystem.id];
    
  const toPos = (toSystem.x !== undefined && toSystem.y !== undefined)
    ? { x: toSystem.x, y: toSystem.y, z: toSystem.z || 0 }
    : distancePositions[toSystem.id];
  
  if (!fromPos || !toPos) return 0;
  
  // Simple 3D distance
  const dx = toPos.x - fromPos.x;
  const dy = toPos.y - fromPos.y;
  const dz = (toPos.z || 0) - (fromPos.z || 0);
  const pixelDistance = Math.sqrt(dx * dx + dy * dy + dz * dz);
  
  // Scale to light years - using a consistent factor
  // This makes the map intuitive: closer on map = closer in distance
  return parseFloat((pixelDistance * GameConfig.ui.distance.pixelToLightYearScale).toFixed(2));
};

/**
 * Get display distance for a system relative to current location
 * @param {Object} system - Target system object
 * @param {Object} currentLocation - Current location object
 * @param {Object} distancePositions - Object mapping system IDs to {x, y, z} positions
 * @returns {number} Distance in light-years
 */
export const getDisplayDistance = (system, currentLocation, distancePositions) => {
  if (!system || !currentLocation) return 0;
  
  // If we're at the target system
  if (currentLocation.id === system.id) return 0;
  
  // Calculate distance between current location and target
  return calculateDistance(currentLocation, system, distancePositions);
};

/**
 * Calculate fuel needed for a journey
 * SINGLE SOURCE OF TRUTH for fuel calculations
 * @param {number} distance - Distance in light-years
 * @param {number} warpFactor - Warp factor (1-9)
 * @param {number} fuelEfficiencyBonus - Fuel efficiency bonus from upgrades (0-1, where 0.1 = 10% less fuel)
 * @returns {number} Fuel needed as percentage
 */
export const calculateFuelNeeded = (distance, warpFactor = 1, fuelEfficiencyBonus = 0) => {
  const warpMultiplier = 1 + (warpFactor - 1) * GameConfig.fuel.warpFuelMultiplier;
  const fuelEfficiencyMultiplier = 1 - fuelEfficiencyBonus;
  
  // Apply crew relationship efficiency bonus
  let crewEfficiencyBonus = 1.0;
  if (typeof window !== 'undefined' && window.CrewRelationshipManager && window.CrewManager) {
    const crew = window.CrewManager.getAllCrew();
    const crewIds = crew.map(c => c.id);
    const efficiencyMultiplier = window.CrewRelationshipManager.getEfficiencyBonus(crewIds);
    crewEfficiencyBonus = 1 / efficiencyMultiplier; // Invert: higher efficiency = less fuel
  }
  
  const baseFuel = distance * GameConfig.fuel.fuelPerLightYear;
  const difficultyMult = getDifficultyMultipliers().fuelConsumptionMultiplier;
  const masteryEfficiency = 1 - getMasteryBonus('engines'); // e.g., 0.97 at level 1
  return Math.ceil(baseFuel * warpMultiplier * GameConfig.fuel.safetyMargin * fuelEfficiencyMultiplier * crewEfficiencyBonus * difficultyMult * masteryEfficiency);
};

/**
 * Calculate optimal warp factor based on distance and available fuel
 * @param {number} distance - Distance in light-years
 * @param {number} availableFuel - Current fuel percentage
 * @param {number} fuelEfficiencyBonus - Fuel efficiency bonus from upgrades (optional)
 * @returns {number} Optimal warp factor (1-9)
 */
export const calculateOptimalWarp = (distance, availableFuel, fuelEfficiencyBonus = 0) => {
  // Start with distance-based warp selection using GameConfig thresholds
  const thresholds = GameConfig.warp.autoModeThresholds;
  let optimalWarp;
  
  if (distance < thresholds.warp2Max) optimalWarp = 2;
  else if (distance < thresholds.warp3Max) optimalWarp = 3;
  else if (distance < thresholds.warp4Max) optimalWarp = 4;
  else if (distance < thresholds.warp5Max) optimalWarp = 5;
  else optimalWarp = 6;
  
  // Now check if we have enough fuel for this warp factor
  // If not, find the highest warp we CAN afford
  for (let testWarp = optimalWarp; testWarp >= 1; testWarp--) {
    const fuelNeeded = calculateFuelNeeded(distance, testWarp, fuelEfficiencyBonus);
    if (fuelNeeded <= availableFuel) {
      return testWarp; // Found a warp factor we can afford
    }
  }
  
  // If we can't afford any warp factor, return 1 (will trigger warning)
  return 1;
};

/**
 * Calculate travel time in seconds based on distance and warp factor
 * @param {number} distance - Distance in light-years
 * @param {number} warpFactor - Warp factor (1-9)
 * @returns {number} Travel time in seconds (clamped between 5-30)
 */
export const calculateTravelTime = (distance, warpFactor) => {
  // Formula: time = distance / (warpFactor^2) with a scaling factor
  const baseTime = distance / Math.pow(warpFactor, 2);
  const scaledTime = Math.max(5, Math.min(30, baseTime * 2)); // Between 5-30 seconds
  return Math.round(scaledTime);
};

/**
 * Get color for threat level
 * @param {string} threat - Threat level string
 * @returns {string} Hex color code
 */
export const getThreatColor = ColorUtils.getNavigationThreatColor;

/**
 * Get color for resource level
 * @param {string} resources - Resource level string
 * @returns {string} Hex color code
 */
export const getResourceColor = ColorUtils.getResourceLevelColor;

/**
 * Generate random planet stars for background animation
 * @param {number} count - Number of stars to generate
 * @returns {Array} Array of star objects with position and animation properties
 */
export const generatePlanetStars = (count = GameConfig.ui.animations.planetStarCount) => {
  const stars = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      id: `planet-star-${i}`,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 0.5,
      opacity: Math.random() * 0.8 + 0.2,
      twinkleDelay: Math.random() * 3
    });
  }
  return stars;
};

/**
 * Generate warp effect elements for animation
 * @param {number} warpFactor - Current warp factor
 * @returns {Array} Array of warp element objects (beams and stars)
 */
export const generateWarpElements = (warpFactor) => {
  const elements = [];
  
  // More elements for higher warp factors
  const starCount = Math.min(GameConfig.ui.animations.warpStarCount, 200 + (warpFactor * 25));
  const beamCount = Math.floor(starCount / 20); // Light beams for tunnel effect
  
  // Generate light beams for tunnel effect
  for (let i = 0; i < beamCount; i++) {
    const angle = (Math.PI * 2 * i) / beamCount; // Evenly distributed around circle
    const length = Math.random() * 60 + 40; // 40-100% length
    
    // Beam properties for 3D tunnel effect
    const hue = Math.random() * 60 + 180; // Blue to cyan range (180-240)
    const brightness = Math.random() * 0.4 + 0.6; // 0.6-1.0 brightness
    
    elements.push({
      id: `beam-${i}`,
      type: 'beam',
      angle,
      length,
      hue,
      brightness,
      width: Math.random() * 3 + 1, // 1-4px width
      animationDuration: 1.5 + Math.random() * 1, // 1.5-2.5 seconds
      delay: Math.random() * 0.5 // 0-0.5 second delay
    });
  }
  
  // Generate stars with better distribution - spread across entire screen
  for (let i = 0; i < starCount; i++) {
    // Start stars spread across the entire viewport, not just center
    const startX = Math.random() * 100; // 0-100% across screen
    const startY = Math.random() * 100; // 0-100% across screen
    
    // Calculate direction from center to star position
    const dx = startX - 50;
    const dy = startY - 50;
    const directionAngle = Math.atan2(dy, dx);
    
    // Calculate end positions - stars fly outward from their starting position
    // Make them travel far enough to definitely leave the viewport
    const travelDistance = 150; // Travel 150% of viewport to ensure they leave screen
    const moveXEnd = startX + Math.cos(directionAngle) * travelDistance;
    const moveYEnd = startY + Math.sin(directionAngle) * travelDistance;
    
    // Convert angle to degrees for CSS rotation
    const angleDeg = (directionAngle * 180 / Math.PI);
    
    // Vary star sizes and colors
    const size = Math.random() * 2 + 0.5; // 0.5-2.5px
    const brightness = Math.random() * 0.6 + 0.4; // 0.4-1.0
    const starColors = [
      `rgba(255, 255, 255, ${brightness})`,
      `rgba(200, 230, 255, ${brightness})`, // Blue-white
      `rgba(255, 240, 200, ${brightness})`, // Warm white
      `rgba(180, 220, 255, ${brightness})`  // Cool white
    ];
    
    elements.push({
      id: `star-${i}`,
      type: 'star',
      startX,
      startY,
      size,
      color: starColors[Math.floor(Math.random() * starColors.length)],
      moveXEnd,
      moveYEnd,
      angle: angleDeg,
      animationDuration: 2 + Math.random() * 2, // 2-4 seconds
      delay: Math.random() * 1.5 // 0-1.5 second delay
    });
  }
  
  return elements;
};

/**
 * Get default warp factor based on navigation mode, distance, and available fuel
 * @param {string} navigationMode - Navigation mode (AUTO/MANUAL/EMERGENCY)
 * @param {number} distance - Distance to destination (optional)
 * @param {number} availableFuel - Current fuel percentage (optional)
 * @returns {number} Default warp factor
 */
export const getDefaultWarpFactor = (navigationMode, distance, availableFuel) => {
  if (navigationMode === 'AUTO' && distance && availableFuel !== undefined) {
    return calculateOptimalWarp(distance, availableFuel);
  } else if (navigationMode === 'EMERGENCY') {
    return 8; // Emergency default: maximum safe speed
  } else {
    return 1; // Manual mode default
  }
};
